// Service layer for PDF Submission feature.
// Wraps all /api/v1/pdf/* API calls.

import apiService from './api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PDFGenerationResult {
  id?: number;
  pdf_url: string;
  metadata_pack: {
    title: string;
    description: string;
    tags: string;
  };
  checklist: string[];
}

export interface PDFStatusData {
  // New website prerequisite data
  brand_name?: string;
  website_url?: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  business_description?: string;
  available_keywords?: string[];
  primary_keyword?: string;
  // Existing website audit data
  total_tracked?: number;
  healthy_links?: number;
  action_required?: boolean;
  submissions?: Array<{
    platform: string;
    url: string;
    status: string;
    freshness_score: string;
  }>;
}

export interface PDFStatusResponse {
  website_state: string;
  data: PDFStatusData;
}

export interface DomainAgeResult {
  url: string;
  age_months: number;
  is_new_website: boolean;
}

export interface PDFTrackPayload {
  platform_name: string;
  live_url: string;
}

export interface PDFTrackResult {
  task_id: string;
  platform: string;
  persisted_url: string;
  status: string;
}

// ── API Functions ─────────────────────────────────────────────────────────────

/**
 * Checks domain age for the user's website URL.
 * Returns whether it is a new website (< 6 months old).
 */
export async function checkDomainAge(url: string): Promise<DomainAgeResult> {
  const res = await apiService.request<DomainAgeResult>(
    `/pdf/check-age?url=${encodeURIComponent(url)}`,
    { method: 'GET' }
  );
  return res;
}

/**
 * Fetches PDF Submission platforms from the database.
 */
export async function getPdfSubmissionPlatforms(): Promise<{ status: string, platforms: any[] }> {
  const res = await apiService.request<{ status: string, platforms: any[] }>(
    `/pdf/platforms`,
    { method: 'GET' }
  );
  return res;
}

/**
 * Fetches prerequisite data (new website) or audit report (existing website).
 */
export async function getPDFStatus(
  userId: string,
  websiteState: 'new_website' | 'existing_website'
): Promise<PDFStatusResponse> {
  const res = await apiService.request<PDFStatusResponse>(
    `/pdf/status?user_id=${encodeURIComponent(userId)}&website_state=${websiteState}`,
    { method: 'GET' }
  );
  return res;
}

/**
 * Triggers AI PDF generation. The backend:
 *  1. Generates content with Gemini
 *  2. Renders a branded PDF with ReportLab
 *  3. Uploads the PDF to AWS S3 (pdf-submission/ folder)
 *  4. Returns a presigned S3 URL valid for 1 hour
 */
export async function generatePDF(payload: {
  user_id: string;
  website_state: 'new_website' | 'existing_website';
  primary_keyword?: string;
}): Promise<PDFGenerationResult> {
  const res = await apiService.request<PDFGenerationResult>(
    '/pdf/generate',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
  return res;
}

/**
 * Tracks a live submission URL for a given platform.
 * Persists the URL to the tracked_pdf_submissions table in the database.
 */
export async function trackSubmission(
  userId: string,
  taskId: string,
  payload: PDFTrackPayload
): Promise<PDFTrackResult> {
  const res = await apiService.request<PDFTrackResult>(
    `/pdf/${encodeURIComponent(taskId)}/track?user_id=${encodeURIComponent(userId)}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
  return res;
}

/**
 * Triggers a browser download from the given AWS S3 presigned URL.
 * The PDF is served directly from AWS S3 — no backend proxy required.
 */
export function downloadPDFFromS3(presignedUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = presignedUrl;
  a.download = filename;
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export interface PDFCompletePayload {
  platforms: string[];
}

export async function completeSubmission(
  submissionId: number,
  payload: PDFCompletePayload
): Promise<{ id: number; published_platforms: string[]; status: string }> {
  const res = await apiService.request<{ id: number; published_platforms: string[]; status: string }>(
    `/pdf/submissions/${submissionId}/complete`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
  return res;
}

export async function updatePDFMetadata(
  submissionId: number,
  payload: { title?: string; description?: string; tags?: string }
): Promise<{ id: number; title: string; description: string; tags: string; status: string }> {
  const res = await apiService.request<{ id: number; title: string; description: string; tags: string; status: string }>(
    `/pdf/submissions/${submissionId}/metadata`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );
  return res;
}

export interface PDFLibraryItem {
  id: number;
  user_id: string;
  pdf_url: string;
  image_url?: string;
  title?: string;
  description?: string;
  tags?: string;
  published_platforms: string[];
  created_at?: string;
  updated_at?: string;
}

export async function getPDFLibrary(): Promise<{ status: string; library: PDFLibraryItem[] }> {
  const res = await apiService.request<{ status: string; library: PDFLibraryItem[] }>(
    `/pdf/library`,
    {
      method: 'GET',
    }
  );
  return res;
}
